/**
 * 15 §3.3 typefaces: register the self-hosted OFL webfonts (Barlow Condensed
 * 500/700, IBM Plex Mono 400) through the FontFace API before the first HUD
 * frame, bounded by a timeout so a slow or blocked fetch never delays the
 * game — the HUD/mono font stacks in ui/components.ts fall back on their own.
 * Pure apart from the injected FontFace/FontSet, so it is node-testable.
 */
export interface FontFile { family: string; weight: '400' | '500' | '700'; url: string }
export interface FontFaceCtor { new (family: string, source: string, desc: { weight: string }): { load(): Promise<unknown> } }
export interface FontSet { add(face: unknown): unknown }
export interface FontLoadResult { loaded: string[]; timedOut: boolean; unavailable: boolean }

export const FONT_TIMEOUT_MS = 1500

export async function loadFonts(files: FontFile[], o: { timeoutMs?: number; FontFace?: FontFaceCtor; fonts?: FontSet } = {}): Promise<FontLoadResult> {
  const g = globalThis as unknown as { FontFace?: FontFaceCtor; document?: { fonts?: FontSet } }
  const FontFace = 'FontFace' in o ? o.FontFace : g.FontFace
  const fonts = 'fonts' in o ? o.fonts : g.document?.fonts
  const timeoutMs = o.timeoutMs ?? FONT_TIMEOUT_MS
  if (!FontFace || !fonts) return { loaded: [], timedOut: false, unavailable: true }
  const done: (string | null)[] = files.map(() => null)
  const all = Promise.all(files.map((f, i) => {
    try {
      const face = new FontFace(f.family, `url(${f.url})`, { weight: f.weight })
      return face.load().then(() => { fonts.add(face); done[i] = `${f.family} ${f.weight}` }, () => { /* a bad file is skipped; the stack falls back */ })
    } catch { return Promise.resolve() }
  }))
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<'timeout'>((r) => { timer = setTimeout(() => r('timeout'), timeoutMs) })
  const outcome = await Promise.race([all.then(() => 'done' as const), timeout])
  if (timer !== undefined) clearTimeout(timer)
  return { loaded: done.filter((x): x is string => x !== null), timedOut: outcome === 'timeout', unavailable: false }
}
